import { FormattedMessage } from 'react-intl';
import { Divider, DividerColor } from '@eliberty/design-system-react';
import classNames from 'classnames';

import { DECIMAL_DISPLAY, FormattedCurrency, ParticipantTitle } from '~atoms';
import { SELECTOR_MODE } from '~constants';
import { AvailableOption, OrderItem, Promotion } from '~types/appsync.types';
import { Participant } from '~types/participant';

import PromotionAlerts from '../PromotionAlerts/promotionAlerts';

export interface PriceDetailsContentProps {
  participants?: Participant[];
  availableOptions?: AvailableOption[];
  orderItems?: OrderItem[];
  currency?: string;
  promotions?: Promotion[];
  basePrice?: number;
  finalPrice?: number;
  productName?: string;
  participantMode: SELECTOR_MODE;
}

/**
 * Presentational content for price details breakdown.
 * Used inside PriceDetailsModal and inside the SelectorDesktop options modal
 * for in-modal navigation (no nested modals).
 */
const PriceDetailsContent = ({
  participants,
  availableOptions,
  orderItems,
  promotions,
  currency,
  basePrice,
  finalPrice,
  productName,
  participantMode,
}: PriceDetailsContentProps) => (
  <div className='PriceDetailsModal_content'>
    <div className='PriceDetailsModal_name'>{productName}</div>
    <div className='PriceDetailsModal_list'>
      <ul>
        {participants?.map((participant: Participant, index: number) => {
          const item = orderItems?.find(
            (orderItem: OrderItem) => orderItem.participantId === participant.id,
          );

          if (!item) {
            return null;
          }

          const hasPromotion = item.maxPublicPrice !== item.total;

          return (
            <li key={`PriceDetailsModal-item-${participant.id}`}>
              <div className='PriceDetailsModal_item'>
                <ParticipantTitle
                  index={index}
                  participant={participant}
                  participantMode={participantMode}
                  allParticipants={participants}
                  inline
                />
                <dt className='sr-only'>
                  <FormattedMessage id='initialPrice' defaultMessage='Initial price' />
                </dt>
                <dd
                  className={classNames({
                    PriceDetailsModal_itemFinalPrices: !hasPromotion,
                  })}
                >
                  <FormattedCurrency
                    value={hasPromotion ? item.maxPublicPrice : item.total}
                    currency={currency!}
                  />
                </dd>
              </div>

              {hasPromotion && (
                <div className='PriceDetailsModal_item'>
                  <div>
                    <FormattedMessage id='discount' defaultMessage='Discount' />
                  </div>
                  <dt className='sr-only'>
                    <FormattedMessage id='youSaved' defaultMessage='You saved' />
                  </dt>
                  <dd className='PriceDetailsModal_discount'>
                    -
                    <FormattedCurrency
                      value={item.maxPublicPrice - item.total}
                      currency={currency!}
                    />
                  </dd>
                </div>
              )}

              {item.children?.map((child) => {
                const option = availableOptions?.find(
                  (availableOption) => availableOption.slug === child.slug,
                );
                return (
                  <div
                    key={`${item.participantId}_${child.type}`}
                    className='PriceDetailsModal_itemOptions'
                  >
                    <span className='PriceDetailsModal_itemOption'>
                      {option ? option.name : child.type}
                    </span>
                    {currency && (
                      <>
                        <dt className='sr-only'>
                          <FormattedMessage id='finalPrice' defaultMessage='Final price' />
                        </dt>
                        <dd>
                          +<FormattedCurrency value={child.total} currency={currency} />
                        </dd>
                      </>
                    )}
                  </div>
                );
              })}

              {hasPromotion && (
                <div className='PriceDetailsModal_item'>
                  <div />
                  <dt className='sr-only'>
                    <FormattedMessage id='discount' defaultMessage='Discount' />
                  </dt>
                  <dd className='PriceDetailsModal_itemFinalPrices'>
                    <FormattedCurrency value={item.total} currency={currency!} />
                  </dd>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>

    <div className='PriceDetailsModal_divider'>
      <Divider color={DividerColor.MediumGrey} />
    </div>
    <div className='PriceDetailsModal_total'>
      <span className='PriceDetailsModal_totalLabel'>
        <FormattedMessage id='total' defaultMessage='Total' />
      </span>

      {basePrice !== undefined && !!basePrice && finalPrice && currency && (
        <dl className='PriceDetailsModal_totalDetail'>
          {finalPrice - basePrice !== 0 && (
            <>
              <div>
                <dt className='sr-only'>
                  <FormattedMessage id='initialPrice' defaultMessage='Initial price' />
                </dt>
                <dd>
                  <FormattedCurrency
                    value={basePrice}
                    currency={currency}
                    decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
                  />
                </dd>
              </div>
              <div>
                <dt className='sr-only'>
                  <FormattedMessage id='youSaved' defaultMessage='You saved' />
                </dt>
                <dd className='PriceDetailsModal_discount'>
                  -
                  <FormattedCurrency
                    value={basePrice - finalPrice}
                    currency={currency}
                    decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
                  />
                </dd>
              </div>
            </>
          )}
          <div>
            <dt className='sr-only'>
              <FormattedCurrency
                value={finalPrice}
                currency={currency}
                decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
              />
            </dt>
            <dd className='PriceDetailsModal_totalFinal'>
              <FormattedCurrency
                value={finalPrice}
                currency={currency}
                decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
              />
            </dd>
          </div>
        </dl>
      )}
    </div>
    <PromotionAlerts
      promotions={promotions}
      className='PriceDetailsModal_promotions'
      showDescriptions
    />
  </div>
);

export default PriceDetailsContent;
